/** @jsxImportSource @emotion/react */
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, CircularProgress, Container, LinearProgress, Typography } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { toast } from '@app/components/ui/Toast/toast'
import { useIconOptions } from '@app/modules/Home/Data/hooks/useIconOptions'
import {
  ALLOWED_METRIC_TYPES,
  DataUpdateFormData,
  DataUpdateSchema,
  ElementFieldConfig,
  buildElementConfig,
  datasetRowToFormValues,
  elementConfigToFieldConfigs,
} from '@app/modules/Home/Data/types'
import { OGR_ACCEPT_ATTR } from '@app/modules/Home/Data/constants/ogr.formats'
import { useGdalExtract } from '@app/modules/Home/Data/hooks/useGdalExtract'
import { getDatasetById, updateDataset } from '@app/services/dataUploadService'
import { DATASETS_QUERY_KEY } from '@app/modules/Home/Data/hooks/useFetchDatasets'
import { useDatasetUpdate } from '@app/modules/Home/Data/hooks/useDatasetUpdate'
import DatasetFormFields from '../components/DatasetFormFields'
import ElementAttributesStep from '../components/ElementAttributesStep'
import FilePickerField from '../components/FilePickerField'
import {
  pageWrapper,
  container,
  formWrapper,
  formOuter,
  fileRow,
} from '../components/DataUpload.styles'
import { groupFiles } from '../utils/fileGroup.utils'
import { GuidePageHeader } from '@app/modules/GuideV2/components/GuidePageHeader'

const DatasetEditPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const iconOptions = useIconOptions()
  const gdalFileInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const gdal = useGdalExtract()

  const [currentConfigs, setCurrentConfigs] = useState<ElementFieldConfig[]>([])
  const [saving, setSaving] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const { update, updating, progress } = useDatasetUpdate()

  const { data: dataset, isLoading } = useQuery({
    queryKey: ['dataset', id],
    queryFn: () => getDatasetById(id!),
    enabled: !!id,
  })

  const initialConfigs = useMemo(
    () => (dataset?.element_config ? elementConfigToFieldConfigs(dataset.element_config) : []),
    [dataset],
  )

  const {
    register,
    watch,
    control,
    handleSubmit,
    getValues,
    resetField,
    formState: { errors },
  } = useForm<DataUpdateFormData>({
    resolver: zodResolver(DataUpdateSchema),
    values: dataset ? (datasetRowToFormValues(dataset) as DataUpdateFormData) : undefined,
  })

  const geometryType = watch('geometryType')

  const handleGdalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (!files.length) return
    await gdal.extract(files, gdalFileInputRef)
  }

  const onSave = handleSubmit(async (formData) => {
    setSaving(true)
    const files = selectedFiles.length > 0 ? groupFiles(selectedFiles).allFiles : []
    const config = currentConfigs.length > 0 ? buildElementConfig(currentConfigs) : undefined
    try {
      if (files.length) {
        await update(id!, files, formData, config)
        await queryClient.invalidateQueries({ queryKey: ['dataset', id] })
      } else {
        const updated = await updateDataset(id!, { metadata: formData, config })
        queryClient.setQueryData(['dataset', id], updated)
      }
      queryClient.invalidateQueries({ queryKey: DATASETS_QUERY_KEY })
      toast({ title: 'Dataset updated', type: 'success', position: 'top-right', duration: 4000 })
      navigate(-1)
    } catch {
      toast({ title: 'Save failed', type: 'error', position: 'top-right', duration: 6000 })
    } finally {
      setSaving(false)
    }
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!dataset) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error.main">Dataset not found.</Typography>
      </Box>
    )
  }

  const datasetName = dataset.Name ?? dataset.library_id

  return (
    <Box css={pageWrapper}>
      <Box css={container}>
        <GuidePageHeader header="Edit Element Dataset" subheader={datasetName} />
        <Box css={formWrapper}>
          <Container>
            <Button variant="text" onClick={() => navigate(-1)} sx={{ mb: 2, pl: 0 }}>
              ← Back
            </Button>

            <Box css={formOuter}>
              <DatasetFormFields
                control={control}
                register={register}
                errors={errors}
                iconOptions={iconOptions}
                geometryType={geometryType}
                onGeometryChange={(field, value) => {
                  const newGeometry =
                    value === '' ? undefined : (value as 'line' | 'point' | 'polygon')
                  field.onChange(newGeometry)
                  const currentMetricType = getValues('metricType')
                  if (
                    newGeometry &&
                    currentMetricType &&
                    !ALLOWED_METRIC_TYPES[newGeometry].includes(currentMetricType)
                  ) {
                    resetField('metricType')
                  }
                }}
              />

              <Box css={fileRow} sx={{ mt: 1 }}>
                <Button component="label" variant="outlined" disabled={gdal.extracting}>
                  Re-extract attributes from file
                  <input
                    ref={gdalFileInputRef}
                    type="file"
                    multiple
                    accept={OGR_ACCEPT_ATTR}
                    style={{ display: 'none' }}
                    onChange={handleGdalFileChange}
                  />
                </Button>
                {gdal.extracting && (
                  <Typography variant="body2" color="text.secondary">
                    Extracting attributes…
                  </Typography>
                )}
              </Box>

              <FilePickerField
                inputRef={fileInputRef}
                selectedFiles={selectedFiles}
                onFilesChange={setSelectedFiles}
                accept={OGR_ACCEPT_ATTR}
                buttonLabel={selectedFiles.length > 0 ? 'Change file(s)' : 'Re-upload file'}
                disabled={updating}
                sx={{ mt: 1 }}
              />

              {gdal.extracting && <LinearProgress variant="indeterminate" />}
              {updating && <LinearProgress variant="determinate" value={progress} />}

              <ElementAttributesStep
                gdalInfo={gdal.gdalInfo}
                gdalFailed={gdal.gdalFailed}
                gdalSkipped={gdal.gdalSkipped}
                initialConfigs={initialConfigs}
                onConfigsChange={setCurrentConfigs}
              />

              <Button
                variant="contained"
                onClick={onSave}
                disabled={saving || gdal.extracting || updating}
                sx={{ alignSelf: 'flex-start' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default DatasetEditPage
